import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Search, Code2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [["product", "Product"], ["category", "Category"], ["brand", "Brand"], ["homepage", "Homepage"]];
const MODES = [["append", "Append to auto schema"], ["replace", "Replace auto schema"], ["disabled", "Disable schema on this page"]];
const sel = "h-9 rounded-md border bg-white px-2 text-sm";

export function SchemaOverrideEditor() {
  const [etype, setEtype] = useState("product");
  const [q, setQ] = useState("");
  const [targets, setTargets] = useState([]);
  const [key, setKey] = useState(null);
  const [data, setData] = useState(null);
  const [raw, setRaw] = useState("");
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (etype === "homepage") return load("home");
    const r = await api.get(`/admin/seo/entity/${etype}/targets`, { params: { q, limit: 20 } });
    setTargets(r.data.items || []);
  };
  const load = async (k) => {
    setKey(k); setTargets([]);
    const r = await api.get("/admin/seo/schema-manager/preview", { params: { type: etype, key: k } });
    setData(r.data); setRaw(r.data.override.custom_jsonld); setMode(r.data.override.jsonld_mode);
  };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/seo/schema-manager/override/${etype}/${encodeURIComponent(key)}`, { custom_jsonld: raw, jsonld_mode: mode }); toast.success("Schema override saved"); await load(key); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };

  return (
    <section data-testid="schema-override-editor">
      <h3 className="font-bold text-steel flex items-center gap-2 mb-1"><Code2 size={18} /> Page / product schema override</h3>
      <p className="text-xs text-muted-foreground mb-3">Preview the exact JSON-LD a page publishes, then add or replace it with your own. Every save is logged in history and can be rolled back.</p>
      <div className="flex flex-wrap gap-2">
        <select value={etype} onChange={(e) => { setEtype(e.target.value); setData(null); setTargets([]); }} className={sel} data-testid="schema-ov-type">
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {etype !== "homepage" && <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search name or SKU" className="h-9 w-64" data-testid="schema-ov-search" />}
        <Button size="sm" variant="outline" onClick={search} data-testid="schema-ov-search-btn"><Search size={14} className="mr-1" /> {etype === "homepage" ? "Load homepage" : "Find"}</Button>
      </div>
      {targets.length > 0 && (
        <div className="mt-2 rounded-xl border bg-white divide-y max-h-56 overflow-y-auto" data-testid="schema-ov-results">
          {targets.map((t) => <button key={t.key} onClick={() => load(t.key)} className="block w-full text-left px-3 py-2 text-sm hover:bg-muted" data-testid={`schema-ov-pick-${t.key}`}>{t.label}</button>)}
        </div>
      )}
      {data && (
        <div className="mt-3 grid lg:grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold text-steel mb-1">Published JSON-LD (effective)</div>
            <pre className="rounded-xl border bg-slate-950 text-emerald-200 text-[11px] p-3 max-h-80 overflow-auto" data-testid="schema-ov-effective">{JSON.stringify(data.effective, null, 2)}</pre>
            <div className="mt-2 space-y-1" data-testid="schema-ov-issues">
              {data.issues.length === 0 ? <div className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={13} /> No validation issues.</div>
                : data.issues.map((i, n) => <div key={n} className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> {i.msg}</div>)}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-steel mb-1">Custom JSON-LD (object or array)</div>
            <Textarea rows={12} value={raw} onChange={(e) => setRaw(e.target.value)} className="font-mono text-[11px]" placeholder='{"@type":"Product", ...}' data-testid="schema-ov-raw" />
            <div className="flex flex-wrap gap-2 mt-2">
              <select value={mode} onChange={(e) => setMode(e.target.value)} className={sel} data-testid="schema-ov-mode">
                {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={() => setRaw(JSON.stringify(data.generated.map(({ "@context": c, ...o }) => o), null, 2))} data-testid="schema-ov-copy-generated">Start from generated</Button>
              <Button size="sm" className="bg-accent text-steel hover:bg-amber-600 font-semibold" onClick={save} disabled={busy} data-testid="schema-ov-save">
                {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Approve &amp; save
              </Button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
